/**
 * RenderFromCadDialog — multi-CAD-image → photoreal render workflow.
 *
 * Designer uploads up to 6 CAD screenshots, picks a metal colour + style
 * tile, optionally adds a brief, and decides whether the resulting
 * renders should land as separate gallery cards or as variant siblings
 * of one piece. Submits as multipart, polls the existing multiview-job
 * endpoint, and hands the merged entries back to the parent.
 *
 * Diamond style auto-applies the rhodium-white prong rule (mandatory).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2, UploadCloud, X, Plus, Sparkles, Layers, FileImage, Diamond,
} from "lucide-react";
import axios, { API } from "@/lib/api";
import { formatApiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const MAX_IMAGES = 6;
const COST_PER_IMAGE = 1;
const MAX_FILE_MB = 12;

const STYLE_TILES = [
  "Diamond", "Jadau", "Polki", "Meenakari", "Kundan",
  "Temple", "Antique", "Rajwadi", "Bridal", "Lightweight", "Modern Fusion",
];

const METALS = [
  { id: "yellow_gold", label: "Yellow Gold" },
  { id: "rose_gold",   label: "Rose Gold" },
  { id: "white_gold",  label: "White Gold" },
  { id: "platinum",    label: "Platinum" },
];

export default function RenderFromCadDialog({
  open,
  onOpenChange,
  creditsRemaining,
  onSubmit, // async (jobId, { count, grouping }) => void  (parent polls + merges)
}) {
  const [files, setFiles] = useState([]); // [{ file, preview, id }]
  const [style, setStyle] = useState("Diamond");
  const [metal, setMetal] = useState("yellow_gold");
  const [briefText, setBriefText] = useState("");
  const [grouping, setGrouping] = useState("separate"); // "separate" | "siblings"
  // cadKind is diamond-only (see backend `_diamond_production_cad_finishing_clause`).
  // Default "render_ready" preserves historical behavior for existing users.
  const [cadKind, setCadKind] = useState("render_ready"); // "render_ready" | "production"
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  // Reset on open. Revoke preview URLs on unmount to avoid leaks.
  useEffect(() => {
    if (open) {
      setFiles([]);
      setStyle("Diamond");
      setMetal("yellow_gold");
      setBriefText("");
      setGrouping("separate");
      setCadKind("render_ready");
      setSubmitting(false);
    }
  }, [open]);
  useEffect(() => () => files.forEach((f) => URL.revokeObjectURL(f.preview)), [files]);

  const addFiles = (incoming) => {
    const list = Array.from(incoming || []);
    if (!list.length) return;
    const room = MAX_IMAGES - files.length;
    if (room <= 0) {
      toast.error(`You can upload up to ${MAX_IMAGES} CAD images.`);
      return;
    }
    const accepted = [];
    for (const f of list.slice(0, room)) {
      if (!f.type.startsWith("image/")) {
        toast.error(`${f.name}: not an image file.`);
        continue;
      }
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        toast.error(`${f.name}: larger than ${MAX_FILE_MB} MB.`);
        continue;
      }
      accepted.push({
        file: f,
        preview: URL.createObjectURL(f),
        id: `${f.name}_${f.size}_${Math.random().toString(36).slice(2, 6)}`,
      });
    }
    if (accepted.length) setFiles((s) => [...s, ...accepted]);
  };

  const removeAt = (idx) =>
    setFiles((s) => {
      const next = s.filter((_, i) => i !== idx);
      URL.revokeObjectURL(s[idx].preview);
      return next;
    });

  const totalCost = files.length * COST_PER_IMAGE;
  const canSubmit = useMemo(
    () => files.length >= 1 && !submitting && creditsRemaining >= totalCost,
    [files.length, submitting, creditsRemaining, totalCost],
  );

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      files.forEach(({ file }) => fd.append("images", file));
      if (style) fd.append("style", style);
      if (metal) fd.append("metal", metal);
      if (briefText.trim()) fd.append("prompt", briefText.trim());
      fd.append("grouping", grouping);
      // cad_kind only matters for Diamond style — backend ignores for others.
      if (style === "Diamond") fd.append("cad_kind", cadKind);

      const { data } = await axios.post(`${API}/studio/render-from-cad`, fd, {
        timeout: 60000,
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!data?.job_id) throw new Error("No job id returned.");
      await onSubmit?.(data.job_id, { count: files.length, grouping });
      onOpenChange?.(false);
    } catch (err) {
      toast.error(formatApiError(err, "CAD render failed to start."));
    } finally {
      setSubmitting(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addFiles(e.dataTransfer?.files);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="render-from-cad-dialog"
        className="max-w-3xl bg-[#0B0B0B] border-white/10 text-white max-h-[88vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#D4AF37]" /> Render from CAD
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            Upload up to {MAX_IMAGES} CAD screenshots — we convert each into a
            photoreal luxury render. {COST_PER_IMAGE} credit per image.
          </DialogDescription>
        </DialogHeader>

        {/* Drop zone + thumbnails */}
        <div
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onDrop={onDrop}
          className="mt-2 rounded-xl border border-dashed border-white/15 bg-white/[0.02] p-4"
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            data-testid="render-cad-file-input"
            onChange={(e) => addFiles(e.target.files)}
          />
          {files.length === 0 ? (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              data-testid="render-cad-drop"
              className="w-full flex flex-col items-center gap-2 py-8 text-white/55 hover:text-white transition"
            >
              <UploadCloud className="w-7 h-7 text-[#D4AF37]" />
              <div className="text-sm">Drop CAD screenshots here or <span className="text-[#D4AF37] underline underline-offset-2">browse</span></div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/35">
                PNG / JPG · up to {MAX_FILE_MB} MB each · max {MAX_IMAGES} files
              </div>
            </button>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-[0.18em] text-white/45">
                  {files.length}/{MAX_IMAGES} CAD image{files.length === 1 ? "" : "s"}
                </div>
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  disabled={files.length >= MAX_IMAGES}
                  className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md border border-[#D4AF37]/35 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Plus className="w-3 h-3" /> Add more
                </button>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {files.map((f, i) => (
                  <div
                    key={f.id}
                    data-testid={`render-cad-thumb-${i}`}
                    className="relative group aspect-square rounded-lg overflow-hidden border border-white/10 bg-black"
                  >
                    <img src={f.preview} alt={f.file.name} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeAt(i)}
                      className="absolute top-1 right-1 p-1 rounded-full bg-black/65 text-white/85 opacity-0 group-hover:opacity-100 transition"
                      aria-label="Remove image"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 px-1.5 py-0.5 text-[9px] text-white/85 bg-gradient-to-t from-black/80 to-transparent truncate">
                      {f.file.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Style tiles */}
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1.5 flex items-center gap-1.5">
            Style
            {style === "Diamond" && (
              <span className="inline-flex items-center gap-1 text-[#D4AF37] normal-case tracking-normal text-[10px]">
                <Diamond className="w-2.5 h-2.5" /> rhodium-white prongs auto-applied
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {STYLE_TILES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStyle(s)}
                data-testid={`render-cad-style-${s.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-[11px] px-2.5 py-1.5 rounded-full border transition ${
                  style === s
                    ? "bg-[#D4AF37]/15 border-[#D4AF37]/55 text-[#D4AF37]"
                    : "border-white/10 text-white/60 hover:border-white/25 hover:text-white"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* CAD file type — Diamond only. Production CAD carries tall
            prong tips + bezel walls that get filed down at the bench;
            enabling this tells the render engine to reduce them to
            finished-product height. See backend
            _diamond_production_cad_finishing_clause. */}
        {style === "Diamond" && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1.5">
              CAD file type
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              <button
                type="button"
                onClick={() => setCadKind("render_ready")}
                data-testid="render-cad-kind-render-ready"
                className={`flex flex-col items-start gap-0.5 text-left px-3 py-2 rounded-lg border transition ${
                  cadKind === "render_ready"
                    ? "bg-[#D4AF37]/10 border-[#D4AF37]/55 text-[#D4AF37]"
                    : "border-white/10 text-white/65 hover:border-white/25 hover:text-white"
                }`}
              >
                <span className="text-[11px] font-medium">Render-ready CAD</span>
                <span className="text-[9px] tracking-wide text-white/45">
                  Prongs &amp; bezel already at finished height — render as-is
                </span>
              </button>
              <button
                type="button"
                onClick={() => setCadKind("production")}
                data-testid="render-cad-kind-production"
                className={`flex flex-col items-start gap-0.5 text-left px-3 py-2 rounded-lg border transition ${
                  cadKind === "production"
                    ? "bg-[#D4AF37]/10 border-[#D4AF37]/55 text-[#D4AF37]"
                    : "border-white/10 text-white/65 hover:border-white/25 hover:text-white"
                }`}
              >
                <span className="text-[11px] font-medium">Production CAD</span>
                <span className="text-[9px] tracking-wide text-white/45">
                  Raw casting file — trim tall prong tips &amp; bezel walls
                </span>
              </button>
            </div>
          </div>
        )}

        {/* Metal + Grouping row */}
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1.5">
              Metal colour
            </div>
            <div className="flex flex-wrap gap-1.5">
              {METALS.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMetal(m.id)}
                  data-testid={`render-cad-metal-${m.id}`}
                  className={`text-[11px] px-2.5 py-1.5 rounded-full border transition ${
                    metal === m.id
                      ? "bg-[#D4AF37]/15 border-[#D4AF37]/55 text-[#D4AF37]"
                      : "border-white/10 text-white/60 hover:border-white/25 hover:text-white"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1.5">
              Group renders as
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              <button
                type="button"
                onClick={() => setGrouping("separate")}
                data-testid="render-cad-group-separate"
                className={`flex flex-col items-start gap-0.5 text-left px-3 py-2 rounded-lg border transition ${
                  grouping === "separate"
                    ? "bg-[#D4AF37]/10 border-[#D4AF37]/55 text-[#D4AF37]"
                    : "border-white/10 text-white/65 hover:border-white/25 hover:text-white"
                }`}
              >
                <span className="text-[11px] font-medium inline-flex items-center gap-1">
                  <FileImage className="w-3 h-3" /> Separate pieces
                </span>
                <span className="text-[9px] tracking-wide text-white/45">Each upload = own card</span>
              </button>
              <button
                type="button"
                onClick={() => setGrouping("siblings")}
                data-testid="render-cad-group-siblings"
                className={`flex flex-col items-start gap-0.5 text-left px-3 py-2 rounded-lg border transition ${
                  grouping === "siblings"
                    ? "bg-[#D4AF37]/10 border-[#D4AF37]/55 text-[#D4AF37]"
                    : "border-white/10 text-white/65 hover:border-white/25 hover:text-white"
                }`}
              >
                <span className="text-[11px] font-medium inline-flex items-center gap-1">
                  <Layers className="w-3 h-3" /> Same piece · views
                </span>
                <span className="text-[9px] tracking-wide text-white/45">Tabs under one card</span>
              </button>
            </div>
          </div>
        </div>

        {/* Extra brief */}
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1.5">
            Extra notes <span className="normal-case tracking-normal text-white/35">(optional — stone notes, finish hints)</span>
          </div>
          <textarea
            value={briefText}
            onChange={(e) => setBriefText(e.target.value)}
            placeholder="e.g. centre stone is a 2ct pear-shape emerald, halo of round diamonds, mirror polish"
            data-testid="render-cad-brief"
            rows={2}
            className="w-full bg-[#0B0B0B] border border-white/10 rounded-md px-3 py-2 text-[12px] text-white placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]/55 resize-none"
          />
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-[11px] text-white/45">
            {files.length} × {COST_PER_IMAGE} = <span className="text-[#D4AF37]">{totalCost} credits</span> · {creditsRemaining} renders left
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onOpenChange?.(false)}
              className="text-[12px] px-3 py-1.5 rounded-md border border-white/10 text-white/65 hover:text-white hover:border-white/25 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              data-testid="render-cad-submit-btn"
              className="text-[12px] inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md border border-[#D4AF37]/55 bg-[#D4AF37] text-black font-medium hover:bg-[#D4AF37]/90 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</>
              ) : (
                <><Sparkles className="w-3.5 h-3.5" /> Render {files.length || ""} CAD {files.length === 1 ? "image" : "images"} · {totalCost} cr</>
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
